import { useEffect, useState } from "react";
import type { AbstractAgent, Message } from "@ag-ui/client";

// Live turn metadata captured for a user message during this session: the
// backend turn id and a client-clock timestamp for when the run started.
export interface LiveTurnMeta {
  turnId: string;
  // Browser-clock ISO timestamp at RunStarted. Stands in for the turn's DB
  // `created_at` until a reload re-serves the authoritative value; the minute
  // granularity of the message-time indicator hides any sub-second skew.
  createdAt: string;
}

// Tracks live AG-UI run_id (== backend trace_id == turn_id) associations for
// each user message that triggered a run during this session.
//
// History-loaded user messages carry `turnId` and `createdAt` in their AG-UI
// extras (see analysis-agent/app/agui/history.py). Newly-submitted user messages
// added client-side don't — `RunStartedEvent` fires once the server starts
// processing them, and the runId on that event is the canonical turn id; we stamp
// the timestamp from the client clock at that moment.
//
// Returns a Map<userMessageId, LiveTurnMeta> consumed by useTurns to attach the
// turn id and timestamp to the assistant turn that follows the user message.
// The most recent user message — the one that triggered the current run.
function mostRecentUserMessageId(messages: ReadonlyArray<Message>): string | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "user") return messages[i].id;
  }
  return undefined;
}

export function useStreamingTurnIds(agent: AbstractAgent): ReadonlyMap<string, LiveTurnMeta> {
  const [map, setMap] = useState<Map<string, LiveTurnMeta>>(() => new Map());

  useEffect(() => {
    const sub = agent.subscribe({
      onRunStartedEvent: ({ event }) => {
        // The most recent user message is the one that triggered this run — it
        // was just appended, before any later one can be.
        const userId = mostRecentUserMessageId(agent.messages as ReadonlyArray<Message>);
        if (userId === undefined) return;
        setMap((prev) => {
          if (prev.get(userId)?.turnId === event.runId) return prev;
          const next = new Map(prev);
          next.set(userId, {
            turnId: event.runId,
            createdAt: new Date().toISOString(),
          });
          return next;
        });
      },
    });
    return () => sub.unsubscribe();
  }, [agent]);

  return map;
}
